import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { assessmentAPI, awsEnvironmentsAPI, projectAPI } from '../services/realApi';

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  route: string;
  section: string;
}

/**
 * Onboarding steps for Hub/Lens
 * Each step has:
 * - id: unique identifier
 * - label: display name
 * - description: what this step accomplishes
 * - route: where to go to complete this step
 * - section: grouping for visual organization
 */
export const HUB_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'assessment',
    label: 'Complete AI Assessment',
    description: "Evaluate your organization's AI strategy and get personalized recommendations",
    route: '/hub/assessments',
    section: 'assess'
  },
  {
    id: 'aws_account',
    label: 'Connect AWS Account',
    description: 'Link your AWS account to unlock cloud management features',
    route: '/hub/aws-environments',
    section: 'setup'
  },
  {
    id: 'first_project',
    label: 'Create First Project',
    description: 'Set up a project to organize and manage your cloud resources',
    route: '/hub/projects',
    section: 'configure'
  }
];

// Icon mapping for steps
export const HUB_STEP_ICONS: Record<string, string> = {
  assessment: 'FiTarget',
  aws_account: 'FiCloud',
  first_project: 'FiFolder'
};

// Section labels
export const HUB_SECTIONS: Record<string, string> = {
  assess: 'Assess',
  setup: 'Setup',
  configure: 'Configure'
};

interface HubOnboardingStatus {
  hasCompletedAssessment: boolean;
  hasAwsAccount: boolean;
  hasProject: boolean;
  loading: boolean;
  error: string | null;
}

type StepStatusMap = Record<string, boolean>;

interface UseHubOnboardingStatusReturn extends HubOnboardingStatus {
  stepStatusMap: StepStatusMap;
  completedSteps: number;
  totalSteps: number;
  completionPercentage: number;
  nextStep: OnboardingStep | undefined;
  isComplete: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to track Hub onboarding progress
 * Checks various APIs to determine which steps are complete
 */
export const useHubOnboardingStatus = (): UseHubOnboardingStatusReturn => {
  const { user } = useAuth();
  const [status, setStatus] = useState<HubOnboardingStatus>({
    hasCompletedAssessment: false,
    hasAwsAccount: false,
    hasProject: false,
    loading: true,
    error: null
  });

  const checkStatus = useCallback(async () => {
    if (!user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const clientId = user?.client_id;
      const isAnonymous = user?.is_anonymous;

      // For anonymous users, skip AWS accounts and projects checks
      // They only need assessment status
      const tenantId = (user as unknown as Record<string, unknown>).tenant_id as string | null ?? null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Multiple API calls return different shapes
      const promises: Promise<any>[] = [
        // Check for completed assessments (status = 'success')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API expects string | undefined
        assessmentAPI.getAssessments(user.role, tenantId, clientId as any).catch(() => []),
      ];

      // Only check AWS accounts and projects for authenticated users
      if (!isAnonymous) {
        promises.push(
          // Check for AWS accounts
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API expects string
          clientId ? awsEnvironmentsAPI.getAccounts(clientId as any).catch(() => []) : Promise.resolve([]),
          // Check for projects
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API expects string
          projectAPI.getProjects(user.role, tenantId, null, clientId as any).catch(() => [])
        );
      }

      const results = await Promise.allSettled(promises);

      // Extract results based on whether we're anonymous or not
      const [assessments, awsAccounts, projects] = isAnonymous
        ? [results[0], { status: 'fulfilled' as const, value: [] }, { status: 'fulfilled' as const, value: [] }]
        : results;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API results have varied shapes
      const getDataArray = (result: PromiseSettledResult<any>): any[] => {
        if (result.status === 'rejected') return [];
        const data = result.value;
        if (Array.isArray(data)) return data;
        return data?.data || data || [];
      };

      const assessmentsList = getDataArray(assessments);
      const awsAccountsList = getDataArray(awsAccounts);
      const projectsList = getDataArray(projects);

      // Check if any assessment is completed (status can be 'success' or 'completed')
      const hasCompletedAssessment = assessmentsList.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have varied shapes
        (a: any) => ['success', 'completed'].includes(a.status) || a.score != null
      );

      setStatus({
        hasCompletedAssessment,
        hasAwsAccount: awsAccountsList.length > 0,
        hasProject: projectsList.length > 0,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error checking Hub onboarding status:', err);
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err)
      }));
    }
  }, [user]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Calculate completion
  const completedSteps = [
    status.hasCompletedAssessment,
    status.hasAwsAccount,
    status.hasProject
  ].filter(Boolean).length;

  const totalSteps = HUB_ONBOARDING_STEPS.length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  // Map step IDs to their completion status
  const stepStatusMap: StepStatusMap = {
    assessment: status.hasCompletedAssessment,
    aws_account: status.hasAwsAccount,
    first_project: status.hasProject
  };

  // Find next incomplete step
  const nextStep = HUB_ONBOARDING_STEPS.find(step => !stepStatusMap[step.id]);
  const isComplete = completedSteps === totalSteps;

  return {
    ...status,
    stepStatusMap,
    completedSteps,
    totalSteps,
    completionPercentage,
    nextStep,
    isComplete,
    refresh: checkStatus
  };
};

export default useHubOnboardingStatus;
